import React, {useEffect, useState} from 'react';
import { Form, Input, Select, Button, Row, } from "antd";
import { UserAddOutlined, EyeTwoTone, EyeInvisibleOutlined} from '@ant-design/icons';
import { PasswordRequirements } from '../../UI/PasswordRequirements.component';
import { updateUser } from '../../../services/UserService';
import { isValidPassword } from '../../../Validation';
import { useNotificationApi } from '../../UI/NotificationProvider';

const { Option } = Select;

export const AddNewUserForm = ({ access, visible, setRefreshData, ...props }) => {

    const [form] = Form.useForm();
    const notificationApi = useNotificationApi();

    const [newPassword, setNewPassword] = useState('');

    useEffect(() => {
    }, [access])

    const renderListItems = (access) => {
        const sortedAccessList = access.sort((a, b) => {
            a = a.companyName || '';
            b = b.companyName || '';
            
            return a.localeCompare(b);
        });

        return sortedAccessList.map((access) => ( 
            { key: access.username, value: access.username, label: access.companyName }
        ))
    }

    const onFinish = async (data) => {
        Object.assign(data, {userID: "", oldusername: undefined})
        try{
            const response = await updateUser(data);

            if(!response){
                throw new Error('Failed to create a new user');
            }

            if (response.message) {
                setTimeout(() => {
                    notificationApi['success']({
                        message: 'Success',
                        description: "User created successfully",  
                        duration: 6,
                    });
                }, 2000)
                setRefreshData(true);
                visible(false);
                return
            } else {
                notificationApi['error']({
                    message: 'Error',
                    description: (response.message) ? response.message : 'Failed to create a new user. Please try again.',  
                    duration: 6,
                })
            }
        } catch (error) {
            notificationApi['error']({
                message: 'Error',
                description: 'Failed to create a new user. Please try again.',  
                duration: 6,
            })
        }
        visible(false);
        return false
    }

    const onCancel = () => {
        form.resetFields();
        visible(false);
    }

    return(
        <>
            <Form
                form={form}
                name='add-new-user-form'
                layout='vertical'
                colon={true}
                onFinish={onFinish}
                autoComplete="off"
            >
                <Form.Item 
                    hasFeedback
                    label='Username / Email Address:' 
                    name="username"
                    rules={[
                        {
                            required: true,
                            message: 'Required',
                            type: 'email',
                        },
                    ]}
                    >
                    <Input type='email'/>
                </Form.Item>
                <Form.Item
                        name="password"
                        label="New Password:"
                        rules={[
                            {
                                required: true,
                                message: 'Required'
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    setNewPassword(value);
                                    if (value && isValidPassword(value)){
                                        return Promise.resolve();
                                    } 
                                    return Promise.reject();
                                    },
                                }),
                            ]}
                        hasFeedback
                        help={ 
                                !isValidPassword(newPassword) && newPassword ? 
                                    <PasswordRequirements password={newPassword}/>
                                : null
                                }
                        >
                        <Input.Password showCount allowClear iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)} />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label="Confirm Password:"
                        dependencies={['account-new-password']}
                        hasFeedback
                        rules={[
                            { 
                                required: true, 
                                message: "Required"
                            }, 
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if(!value || getFieldValue('password') === value){
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('The password that you just entered does not match new password.'))
                                }
                            }),
                        ]}
                        >
                        <Input.Password allowClear iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)} />
                    </Form.Item>
                <Form.Item 
                    label='Privileges:' 
                    name="privilege"
                    rules={[
                        {
                            required: true,
                            message: 'Required',
                        },
                    ]}
                    >
                    <Select>
                        <Select.Option value='R'>Read</Select.Option>
                        <Select.Option value='W'>Read/Write</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item 
                    label='Access:' 
                    name="access"
                    rules={[
                        {
                            required: false,
                            message: 'Required',
                        },
                    ]}
                    >
                    <Select
                        mode='multiple'
                        allowClear
                        showSearch
                        options={renderListItems(access)}
                        optionFilterProp="label"
                        filterOption={(input, option) => {
                                if(option.label){
                                    return (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                } else {
                                    return (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                                }
                            }
                        }
                    />
                </Form.Item>
                <Form.Item noStyle>
                    <Row justify="end" style={{gap: 8}}>
                        <Button onClick={() => onCancel()}>Cancel</Button>
                        <Button type='primary' htmlType='submit' icon={<UserAddOutlined/>}>Add New User</Button>
                    </Row>
                </Form.Item>
            </Form>
        </>
    );
}